import { APIConfig } from "@/lib/api";
import { request as __request } from "@/lib/api/request";
import { z } from "zod";

const customerManagementSchema = z.object({
  id: z.coerce.number(),
  supplier: z.coerce.number(),
  erp_contact_id: z.string().nullable().optional().default(""),
  contact_name: z.string().default(""),
  company_name: z.string().default(""),
  first_name: z.string().default(""),
  last_name: z.string().default(""),
  email: z.string().nullable().optional().default(""),
  phone: z.string().default(""),
  mobile: z.string().default(""),
  address: z.string().default(""),
  deliveries_count: z.coerce.number().default(0),
  credit_limit: z
    .union([z.number(), z.string()])
    .nullable()
    .optional()
    .default(null),
  invoice_frequency_flag: z.string().default(""),
  default_currency: z.string().default(""),
  created_at: z.string(),
  updated_at: z.string(),
});

const paginatedCustomerManagementSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(customerManagementSchema),
});

const customerPriceHistoryEntrySchema = z.object({
  product_key: z.string(),
  product_name: z.string(),
  product_sku: z.string().default(""),
  qty_sold: z.union([z.number(), z.string()]),
  min_price: z.union([z.number(), z.string()]),
  max_price: z.union([z.number(), z.string()]),
  latest_price: z.union([z.number(), z.string()]),
  average_price: z.union([z.number(), z.string()]),
  median_price: z.union([z.number(), z.string()]),
  currency_code: z.string().default(""),
});

const customerSyncSummarySchema = z.object({
  total: z.number(),
  processed: z.number(),
  created: z.number(),
  updated: z.number(),
});

const customerSyncResponseSchema = z.object({
  status: z.literal("completed"),
  summary: customerSyncSummarySchema,
});

const optionalTrimmedStringSchema = z.string().trim().optional();
const optionalEmailSchema = z
  .string()
  .trim()
  .refine(
    (value) => !value || z.string().email().safeParse(value).success,
    "Enter a valid email address",
  )
  .transform((value) => value.toLowerCase())
  .optional();
const optionalErpContactIdSchema = z
  .string()
  .trim()
  .nullable()
  .optional()
  .transform((value) => (value === undefined ? undefined : value || null));
const optionalCreditLimitSchema = z
  .union([z.number(), z.string().trim(), z.null()])
  .optional()
  .refine(
    (value) =>
      value === undefined ||
      value === null ||
      value === "" ||
      (Number.isFinite(Number(value)) && Number(value) >= 0),
    "Credit limit must be zero or greater",
  )
  .transform((value) => (value === "" ? null : value));
const optionalInvoiceSettingSchema = z
  .string()
  .trim()
  .refine(
    (value) => ["", "cod", "net_15", "net_30"].includes(value),
    "Unsupported invoice setting",
  )
  .optional();
const optionalCurrencySchema = z
  .string()
  .trim()
  .refine(
    (value) => !value || /^[A-Za-z]{3}$/.test(value),
    "Currency must be a three-letter ISO code",
  )
  .transform((value) => value.toUpperCase())
  .optional();

const customerWritePayloadSchema = z.object({
  erp_contact_id: optionalErpContactIdSchema,
  contact_name: optionalTrimmedStringSchema,
  company_name: z.string().trim().min(1),
  first_name: optionalTrimmedStringSchema,
  last_name: optionalTrimmedStringSchema,
  email: optionalEmailSchema,
  phone: optionalTrimmedStringSchema,
  mobile: optionalTrimmedStringSchema,
  address: optionalTrimmedStringSchema,
  credit_limit: optionalCreditLimitSchema,
  invoice_frequency_flag: optionalInvoiceSettingSchema,
  default_currency: optionalCurrencySchema,
});
const partialCustomerWritePayloadSchema = customerWritePayloadSchema.partial();

export type CustomerManagementRecord = z.infer<typeof customerManagementSchema>;
export type CustomerManagementPage = z.infer<
  typeof paginatedCustomerManagementSchema
>;
export type CustomerPriceHistoryEntry = z.infer<
  typeof customerPriceHistoryEntrySchema
>;
export type CustomerWritePayload = z.infer<typeof customerWritePayloadSchema>;
export type CustomerSyncResponse = z.infer<typeof customerSyncResponseSchema>;

export class CustomerManagementService {
  static async listPage({
    page = 1,
    pageSize = 10,
    search = "",
  }: {
    page?: number;
    pageSize?: number;
    search?: string;
  } = {}): Promise<CustomerManagementPage> {
    const normalizedPage = Number.isFinite(page)
      ? Math.max(1, Math.trunc(page))
      : 1;
    const normalizedPageSize = Number.isFinite(pageSize)
      ? Math.min(50, Math.max(1, Math.trunc(pageSize)))
      : 10;
    const query: Record<string, string> = {
      page: String(normalizedPage),
      page_size: String(normalizedPageSize),
    };
    if (search.trim()) {
      query.search = search.trim();
    }
    return __request(
      APIConfig,
      {
        method: "GET",
        url: "/api/v1/inventory/customers-management/",
        query,
      },
      paginatedCustomerManagementSchema,
    );
  }

  static async create(
    payload: CustomerWritePayload,
  ): Promise<CustomerManagementRecord> {
    const normalizedPayload = customerWritePayloadSchema.parse(payload);
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/customers-management/",
        body: normalizedPayload,
      },
      customerManagementSchema,
    );
  }

  static async update(
    customerId: number,
    payload: Partial<CustomerWritePayload>,
  ): Promise<CustomerManagementRecord> {
    const normalizedPayload = partialCustomerWritePayloadSchema.parse(payload);
    return __request(
      APIConfig,
      {
        method: "PATCH",
        url: `/api/v1/inventory/customers-management/${customerId}/`,
        body: normalizedPayload,
      },
      customerManagementSchema,
    );
  }

  static async getPriceHistory(
    customerId: number,
  ): Promise<CustomerPriceHistoryEntry[]> {
    return __request(
      APIConfig,
      {
        method: "GET",
        url: `/api/v1/inventory/customers-management/${customerId}/price-history/`,
      },
      z.array(customerPriceHistoryEntrySchema),
    );
  }

  static async syncWithErp(): Promise<CustomerSyncResponse> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/sync-customers/",
      },
      customerSyncResponseSchema,
    );
  }
}
